import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { EmptyStateComponent } from 'components/EmptyStateComponent';
import { Heading } from 'components/Heading';
import { Icon } from 'components/Icon';
import { MobileScreen } from 'components/MobileScreen';
import { ScoreBadge } from 'components/ScoreBadge';
import { Spacer } from 'components/Spacer';
import { requireGameLoaded } from 'engine/api';
import { useStore, useTasks } from 'engine/store';
import {
	FerrisWheel,
	Camera,
	ChevronRight,
	ClipboardPenLine,
	FileQuestion,
	Info,
	ListChecks,
	ListIcon,
	NotebookPen,
	Rotate3D,
	Sparkles,
	Video,
	type LucideIcon,
} from 'lucide-react';
import type { FC } from 'react';
import type { TaskStatus } from 'shared/modelTypes/client';
import type { GameRound, GameTask } from 'shared/modelTypes/game';
import { unreachable } from 'shared/util';

export const Route = createFileRoute('/rounds/$roundId/')({
	component: RouteComponent,
	beforeLoad: requireGameLoaded,
});

function RouteComponent() {
	const params = Route.useParams();
	const navigate = useNavigate();

	const game = useStore((s) => s.game);
	const round = game?.rounds.find((r) => r.id == params.roundId);

	return (
		<>
			{!round && (
				<EmptyStateComponent
					icon={FileQuestion}
					title="Round not found"
					action={{ label: 'Return to rounds', onClick: () => navigate({ to: '/rounds' }) }}
				/>
			)}
			{round && <TaskListScreen round={round} />}
		</>
	);
}

export const TabButton: FC<{ active: boolean; label: string; count?: number; onClick: () => void }> = ({
	active,
	label,
	count = 0,
	onClick,
}) => {
	return (
		<button data-active={active ? true : undefined} onClick={onClick} className={'group relative flex-1 shrink py-3 text-sm font-bold'}>
			<span className="inline-flex gap-2">
				<span>{label}</span>
				{count > 0 && <span className="opacity-70">{count}</span>}
			</span>
			<div className="absolute bottom-0 h-1 w-full bg-transparent group-data-[active]:bg-[var(--skin-text)]" />
		</button>
	);
};

const TaskNav: FC<{ round: GameRound }> = ({ round }) => {
	const taskStates = useTasks(round.id);
	const stateCounts = taskStates?.reduce(
		(acc, taskState) => {
			if (taskState.unlocked) acc[taskState.status]++;

			return acc;
		},
		{ todo: 0, 'in-progress': 0, done: 0 } as Record<TaskStatus, number>,
	);

	const activeTaskTab = useStore((s) => s.activeTaskTab);
	const setActiveTaskTab = useStore((s) => s.setActiveTaskTab);
	console.log('stateCounts', stateCounts);

	return (
		<div className="pt-2 shadow-md skin-bg safe-area-h">
			<div className="px-6">
				<Heading mode="secondary">{round.title}</Heading>
			</div>
			<div className="flex">
				<TabButton
					onClick={() => setActiveTaskTab('todo')}
					active={activeTaskTab === 'todo'}
					count={stateCounts?.todo ?? 0}
					label={__`To do`}
				/>
				<TabButton
					active={activeTaskTab === 'in-progress'}
					count={stateCounts?.['in-progress'] ?? 0}
					label={__`In progress`}
					onClick={() => setActiveTaskTab('in-progress')}
				/>
				<TabButton
					active={activeTaskTab === 'done'}
					count={stateCounts?.done ?? 0}
					label={__`Completed`}
					onClick={() => setActiveTaskTab('done')}
				/>
			</div>
		</div>
	);
};

export const TaskListScreen: FC<{ round: GameRound }> = ({ round }) => {
	return (
		<MobileScreen className="flex flex-col gap-4" nav={<TaskNav round={round} />}>
			<TaskList round={round} />
		</MobileScreen>
	);
};

const TaskList: FC<{ round: GameRound }> = ({ round }) => {
	const activeTaskTab = useStore((s) => s.activeTaskTab);

	const tasks = useTasks(round.id, activeTaskTab);
	const inProgressTasks = useTasks(round.id, 'in-progress');
	const unlockedTasks = tasks?.filter((t) => t.unlocked);

	const progressState = activeTaskTab == 'todo' ? inProgressTasks?.length || 0 > 0 : false;

	let icon: LucideIcon = ListIcon;
	if (activeTaskTab == 'todo') icon = Sparkles;

	let label = 'To do';
	if (activeTaskTab == 'done') label = 'Completed';
	if (activeTaskTab == 'in-progress') label = 'In progress';

	return (
		<>
			{unlockedTasks && unlockedTasks.length == 0 ? (
				progressState ? (
					<EmptyStateComponent icon={ClipboardPenLine} title={__`Waiting for review.`} />
				) : (
					<EmptyStateComponent icon={icon} title={__`No tasks ${label}`} />
				)
			) : (
				''
			)}
			{unlockedTasks?.map((task) => (
				<Link
					key={task.id}
					to="/rounds/$roundId/task/$taskId"
					params={{ roundId: round.id, taskId: task.id }}
					viewTransition={{ types: ['slide-left'] }}
					className="flex cursor-pointer select-none items-center border p-3 shadow-md skin-bg-button skin-border-button skin-text-button skin-rounded-lg"
				>
					<div className="flex flex-col gap-2">
						<Heading mode="secondary">{task.title}</Heading>
						<ScoreBadge>
							{task.points} {__`points`}
						</ScoreBadge>
					</div>
					<Spacer />
					<Icon className="mr-2 opacity-90" icon={iconForTask(task)} />
					<ChevronRight />
				</Link>
			))}
		</>
	);
};

function iconForTask(task: GameTask): LucideIcon {
	switch (task.type) {
		case 'augmented-reality':
			return Rotate3D;
		case 'information':
			return Info;
		case 'automatic-validation':
			switch (task.typeAutomaticValidationConfig!.kind) {
				case 'open':
					return NotebookPen;
				case 'multiple-choice':
					return ListChecks;
				case 'multi-choice':
					return ListChecks;
				default:
					unreachable(task.typeAutomaticValidationConfig!.kind);
			}
			break;
		case 'validation-by-game-master':
			switch (task.typeGameMasterValidationConfig!.kind) {
				case 'open':
					return NotebookPen;
				case 'photo':
					return Camera;
				case 'video':
					return Video;
				default:
					unreachable(task.typeGameMasterValidationConfig!.kind);
			}
			break;
		case 'fullscreen-video':
			return Video;
		case 'bingo':
			return FerrisWheel;
		default:
			unreachable(task.type);
	}
}
